import React, { useState } from 'react';
import TypingResponse from '../../typingResponse/TypingResponse';
import DocumentRenderer from '../../documentRenderer/DocumentRenderer';
import AccordionItem from './AccordionItem';
import {
  formatKey,
  formatTabLabel,
  formatValue,
  TRY_MODE,
} from '../../../common/Utils/Utils';
import '../../pages/tryNow/DynamicResultRenderer.css';

/**
 * DynamicResultRenderer Component
 *
 * Displays API response in a tabbed interface:
 * - Result Tab: Clean formatted extracted data
 * - Documents Tab: Document images (if available)
 * - Response Tab: Full JSON tree view
 *
 * @param {Object} apiResponse - The API response
 * @param {File} selectedFile - The original uploaded file
 */
const DynamicResultRenderer = ({
  apiResponse,
  selectedFile,
  mode = TRY_MODE.SINGLE,
}) => {
  console.log('🔍 DynamicResultRenderer - Raw API Response:', apiResponse);

  const [activeTab, setActiveTab] = useState('');
  const [openAccordions, setOpenAccordions] = useState({});

  // Toggle accordion
  const toggleAccordion = (key) => {
    setOpenAccordions((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // Check if data is nested (contains objects as values)
  const isNestedData = (data) => {
    if (typeof data !== 'object' || Array.isArray(data)) return false;
    return Object.values(data).some(
      (value) => typeof value === 'object' && value !== null
    );
  };

  // Extract data array from response
  const dataArray = apiResponse?.data;

  if (!Array.isArray(dataArray)) {
    console.warn('⚠️ Expected data to be an array, got:', typeof dataArray);
    return <TypingResponse data={apiResponse} />;
  }

  // Build tabs dynamically from the response data structure
  const tabs = [];
  const tabData = {};

  dataArray.forEach((item) => {
    Object.keys(item).forEach((key) => {
      // Skip null or undefined values, and don't create tabs for them
      if (
        item[key] !== null &&
        item[key] !== undefined &&
        !tabs.includes(key)
      ) {
        // Skip empty objects
        if (
          typeof item[key] === 'object' &&
          !Array.isArray(item[key]) &&
          Object.keys(item[key]).length === 0
        ) {
          return; // Skip this tab
        }

        // Skip empty arrays
        if (Array.isArray(item[key]) && item[key].length === 0) {
          return; // Skip this tab
        }

        tabs.push(key);
        tabData[key] = item[key];
      }
    });
  });

  // Always add "Full Response" tab at the end
  tabs.push('JSON');

  // Set initial active tab if not set
  if (!activeTab && tabs.length > 0) {
    setActiveTab(tabs[0]);
  }

  // Render content based on tab type
  const renderTabContent = (tabKey) => {
    // Full Response tab - always show JSON viewer
    if (tabKey === 'JSON') {
      return <TypingResponse data={apiResponse} />;
    }

    const data = tabData[tabKey];

    // If data is null or undefined, skip rendering
    if (data === null || data === undefined) {
      return (
        <div className="data-display-container">
          <p className="text-center text-muted">No data available</p>
        </div>
      );
    }

    // If data is an array of documents
    if (tabKey === 'documents' && Array.isArray(data) && data.length > 0) {
      // Check if all documents are images
      const allImages = data.every((doc) => {
        const docType = doc.document_type || doc.documentType || '';
        return docType.toLowerCase().includes('image');
      });

      return (
        <div className="documents-container">
          <div className={`documents-grid ${allImages ? 'images-grid' : ''}`}>
            {data.map((doc, index) => (
              <DocumentRenderer key={index} document={doc} />
            ))}
          </div>
        </div>
      );
    }

    // If data is extractedValues or extractedData or similar object
    if (typeof data === 'object' && !Array.isArray(data)) {
      // Check if this is nested data (tree structure)
      if (isNestedData(data)) {
        return (
          <div className="data-display-container">
            <div className="accordion-container">
              {Object.entries(data).map(([key, value]) => (
                <AccordionItem
                  key={key}
                  title={key}
                  data={value}
                  isOpen={openAccordions[key]}
                  onToggle={() => toggleAccordion(key)}
                />
              ))}
            </div>
          </div>
        );
      }

      // Flat data structure - show as simple list
      return (
        <div className="data-display-container">
          <div className="data-list">
            {Object.entries(data).map(([key, value]) => (
              <div key={key} className="br_7 data-row flex-column p-2">
                <span className="data-label font_500 font_13">
                  {formatKey(key)}
                </span>
                <span className="data-value font_500 font_15">
                  {formatValue(value)}
                </span>
              </div>
            ))}
          </div>
        </div>
      );
    }

    // Default: show as JSON
    return <TypingResponse data={data} />;
  };

  return (
    <div
      className="dynamic-result-container"
      style={{
        height:
          mode === TRY_MODE.SINGLE
            ? 'calc(100svh - 295px)'
            : 'calc(100svh - 335px)',
      }}
    >
      {/* Tabs - Dynamic based on response structure */}
      <div className="tabs-container">
        {tabs.map((tabKey) => (
          <button
            key={tabKey}
            className={`tab-button ${activeTab === tabKey ? 'active' : ''}`}
            onClick={() => setActiveTab(tabKey)}
          >
            {formatTabLabel(tabKey)}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="tab-content">{renderTabContent(activeTab)}</div>
    </div>
  );
};

export default DynamicResultRenderer;
